import { useState, useEffect, useRef, useCallback, memo } from "react";
import { cn } from "@/lib/utils";
import {
  getOptimizedUrl,
  getBlurPlaceholderUrl,
  generateSrcSet,
  generateSizes,
  isCloudinaryUrl,
  preloadImage,
  type CloudinaryTransformOptions,
} from "@/utils/cloudinaryUrl";

export interface OptimizedImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "placeholder"> {
  /** Image source URL */
  src: string;
  /** Alt text for accessibility */
  alt: string;
  /** Width of the image */
  width?: number;
  /** Height of the image */
  height?: number;
  /** Whether this is a priority/above-the-fold image (preloads) */
  priority?: boolean;
  /** Placeholder type */
  placeholder?: "blur" | "empty" | "color";
  /** Custom placeholder color (hex) */
  placeholderColor?: string;
  /** Aspect ratio (e.g., "16/9", "4/3", "1/1") */
  aspectRatio?: string;
  /** Object fit style */
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
  /** Object position */
  objectPosition?: string;
  /** Custom sizes attribute for responsive images */
  sizes?: string;
  /** Custom responsive widths for srcset generation */
  responsiveWidths?: number[];
  /** Whether to use lazy loading (default: true, unless priority) */
  lazy?: boolean;
  /** Cloudinary transformation options */
  transformOptions?: CloudinaryTransformOptions;
  /** Callback when image loads */
  onLoad?: () => void;
  /** Callback on error */
  onError?: () => void;
}

/**
 * OptimizedImage Component
 * 
 * A performant image component with:
 * - Lazy loading with Intersection Observer
 * - Blur placeholder during load
 * - Responsive srcset for Cloudinary images
 * - Priority preloading for above-the-fold images
 * - WebP/AVIF format optimization via Cloudinary
 */
export const OptimizedImage = memo(function OptimizedImage({
  src,
  alt,
  width,
  height,
  priority = false,
  placeholder = "blur",
  placeholderColor = "#e5e7eb",
  aspectRatio,
  objectFit = "cover",
  objectPosition = "center",
  sizes,
  responsiveWidths,
  lazy = true,
  transformOptions,
  onLoad,
  onError,
  className,
  style,
  ...props
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Generate optimized URLs
  const optimizedSrc = getOptimizedUrl(src, {
    width,
    height,
    ...transformOptions,
  });
  
  const blurPlaceholder = placeholder === "blur" && isCloudinaryUrl(src)
    ? getBlurPlaceholderUrl(src)
    : null;

  const srcSet = isCloudinaryUrl(src)
    ? generateSrcSet(src, responsiveWidths)
    : undefined;
  
  const computedSizes = sizes || generateSizes();

  // Preload priority images
  useEffect(() => {
    if (priority && src) {
      preloadImage(src, { width, ...transformOptions });
    }
  }, [priority, src, width, transformOptions]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !lazy) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "200px", // Start loading 200px before entering viewport
        threshold: 0,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority, lazy]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    onError?.();
  }, [onError]);

  // Compute container styles
  const containerStyles: React.CSSProperties = {
    position: "relative",
    overflow: "hidden",
    ...(aspectRatio && { aspectRatio }),
    ...(width && !aspectRatio && { width }),
    ...(height && !aspectRatio && { height }),
    ...style,
  };

  // Placeholder styles
  const placeholderStyles: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit,
    objectPosition,
    transition: "opacity 0.3s ease-in-out",
    opacity: isLoaded ? 0 : 1,
    pointerEvents: "none",
  };

  // Main image styles
  const imageStyles: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit,
    objectPosition,
    transition: "opacity 0.3s ease-in-out",
    opacity: isLoaded ? 1 : 0,
  };

  // Error fallback
  if (hasError) {
    return (
      <div
        ref={containerRef}
        className={cn(
          "flex items-center justify-center bg-muted text-muted-foreground",
          className
        )}
        style={containerStyles}
      >
        <span className="text-sm">Failed to load image</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      style={containerStyles}
    >
      {/* Placeholder */}
      {placeholder === "blur" && blurPlaceholder && (
        <img
          src={blurPlaceholder}
          alt=""
          aria-hidden="true"
          style={placeholderStyles}
          className="blur-lg scale-110"
        />
      )}
      
      {placeholder === "color" && (
        <div
          aria-hidden="true"
          style={{
            ...placeholderStyles,
            backgroundColor: placeholderColor,
          }}
        />
      )}

      {/* Main Image */}
      {isInView && (
        <img
          ref={imgRef}
          src={optimizedSrc}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          fetchPriority={priority ? "high" : "auto"}
          srcSet={srcSet}
          sizes={srcSet ? computedSizes : undefined}
          onLoad={handleLoad}
          onError={handleError}
          style={imageStyles}
          {...props}
        />
      )}

      {/* Loading skeleton when not in view */}
      {!isInView && placeholder === "empty" && (
        <div
          className="absolute inset-0 animate-pulse bg-muted"
          aria-hidden="true"
        />
      )}
    </div>
  );
});

export default OptimizedImage;
